import { Brain, Database, FolderCog, Search } from 'lucide-react';
import * as React from 'react';
import { ReactNode } from 'react';
import { Control, useFieldArray, UseFormGetValues, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { useTranslate } from 'sbx-crm-menu-ui';
import ButtonComponent from 'src/components/shared/ButtonComponent';
import { SourcesAnswerComponent } from 'src/components/shared/SourcesAnswerComponent';
import { AppAgentContext } from 'src/context/ContextProvider';
import { reviewRagService } from 'src/services/AgentService';
import { appActions } from 'src/store';
import { ModalType } from 'src/types/Modal';
import sbxLogo from '../../../../../assets/images/sbxlogo.svg';
import { AiAgentV2Data, SkillType } from '../../../../../types/AiAgentV2';
import RenderFormFieldsComponent, {
    FormFieldGroup,
    GroupTypes,
} from '../../../../shared/renderFormFieldsComponent/RenderFormFieldsComponent';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../ui/accordion';
import { Card, CardContent } from '../../../../ui/card';
import { SkillForm } from './AgentSkillsComponent';
import SkillKeyValueConfigComponent from './SkillKeyValueConfigComponent';
import { EnumSkillRetrieverFields } from 'src/types/Common';

type Props = {
    control: Control<SkillForm>;
    name?: string;
    register: UseFormRegister<SkillForm>;
    customRetrieverFields: (name: string, group?:string) => ReactNode;
    skillRetrieverFields: GroupTypes<keyof AiAgentV2Data>;
    structured?: boolean;
    watch?: UseFormWatch<SkillForm>;
    getValues?: UseFormGetValues<SkillForm>;
};
const SkillRetrieverComponent = ({ control, skillRetrieverFields, customRetrieverFields, register, name = "config", structured, watch, getValues }: Props) => {
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { dispatch } = React.useContext(AppAgentContext);
    const [loadingDocument, setLoadingDocument] = React.useState(false);

    const columnsToFind = useFieldArray({
        name: `new_skill.${name}.columns_to_find` as any,
        control,
    });

    const columnsToReturn = useFieldArray({
        name: `new_skill.${name}.columns_to_return` as any,
        control,
    });


    const fieldsToFind = useFieldArray({
        name: `new_skill.${name}.fields_to_find` as any,
        control,
    });

    const fieldsToReturn = useFieldArray({
        name: `new_skill.${name}.fields_to_return` as any,
        control,
    });

    const getIconByGroup = (group: string) => {
        switch (group) {
            case 'database':
                return <Database className="w-6 h-6 " />;
            case 'vector_storage':
                return <FolderCog className="w-6 h-6 " />;
            case 'sbx':
                return <img src={sbxLogo} alt="" style={{
                    width: '24px',
                    height: '24px',
                }} />;
            default:
                return null;
        }
    };

    const renderGroupFields = (group: string) => {


        if (group === EnumSkillRetrieverFields.DATABASE) {
            return <>
                {!structured && <SkillKeyValueConfigComponent label={agent('columns_to_find')}
                                              placeholderKey={common('name')}
                                              placeholderValue={common('description')}
                                              append={() => {
                                                  columnsToFind.append({
                                                      name: '',
                                                      value: '',
                                                  });
                                              }}
                                              config={name}
                                              name={`columns_to_find`}
                                              buttonLabel={`${common('add')} ${agent('columns_to_find')}`}
                                              formArray={columnsToFind}
                                              register={register} />
                }
                <SkillKeyValueConfigComponent label={agent('columns_to_return')}
                                              placeholderKey={common('name')}
                                              placeholderValue={common('description')}
                                              append={() => {
                                                  columnsToReturn.append({
                                                      name: '',
                                                      value: '',
                                                  });
                                              }}
                                              config={name}
                                              name={`columns_to_return`}
                                              buttonLabel={`${common('add')} ${agent('columns_to_return')}`}
                                              formArray={columnsToReturn}
                                              register={register} />
            </>;
        }

        if (group === EnumSkillRetrieverFields.SBX) {
            return <>
                {!structured && <SkillKeyValueConfigComponent label={agent('fields_to_find')}
                                              placeholderKey={common('name')}
                                              placeholderValue={common('description')}
                                              append={() => {
                                                  fieldsToFind.append({
                                                      name: '',
                                                      value: '',
                                                  });
                                              }}
                                              config={name}
                                              name={`fields_to_find`}
                                              buttonLabel={`${common('add')} ${agent('fields_to_find')}`}
                                              formArray={fieldsToFind}
                                              register={register} />
                }
                <SkillKeyValueConfigComponent label={agent('fields_to_return')}
                                              placeholderKey={common('name')}
                                              placeholderValue={common('description')}
                                              append={() => {
                                                  fieldsToReturn.append({
                                                      name: '',
                                                      value: '',
                                                  });
                                              }}
                                              config={name}
                                              name={`fields_to_return`}
                                              buttonLabel={`${common('add')} ${agent('fields_to_return')}`}
                                              formArray={fieldsToReturn}
                                              register={register} />
            </>;
        }

        return null;
    };

    const RenderField = ({group, currentGroup}: {group:string, currentGroup: {
        label?: React.ReactNode;
        columns: string;
        items: FormFieldGroup<keyof AiAgentV2Data>[];
    }}) => {
        return (
            <Card className="mb-3">
                <CardContent>
                    <AccordionItem value={`item_${group}`}>
                        <AccordionTrigger
                            className="text-lg font-semibold text-purple-700 hover:text-purple-900 capitalize">
                            <div className="flex items-center gap-2">
                                {getIconByGroup(group)}
                                {group === EnumSkillRetrieverFields.SBX ? 'SBX' : agent(group) ?? group}
                            </div>

                        </AccordionTrigger>
                        <AccordionContent>
                            {group === EnumSkillRetrieverFields.EXCEL ?
                            <div
                            className={`grid grid-cols-1 gap-3 mb-3`}>
                                {customRetrieverFields(name, group)}
                            </div> 
                            :                           
                            <div
                                className={`grid ${currentGroup.columns ?? 'grid-cols-1 '} gap-3 mb-3`}>
                                <RenderFormFieldsComponent
                                    fields={currentGroup.items}
                                    control={control}
                                />

                                {renderGroupFields(group)}
                            </div> }
                        </AccordionContent>
                    </AccordionItem>
                </CardContent>
            </Card>
        )
    }

    const defaultValue = React.useMemo(() => {
        return structured ? 'item_database' : 'item_general_knowledge';
    }, [structured]);

    const showDataVector = async() => {
        setLoadingDocument(true);
        const res = await reviewRagService({
            config: getValues?.('new_skill.config.vector_store_config') || {},
            folder: getValues?.('new_skill.config.folder') || null,
            url: getValues?.('new_skill.config.url') || null
        });

        if(res.success) {
            setLoadingDocument(false);
            dispatch(appActions.openModal({
                type: ModalType.DYNAMIC,
                data: {
                    title: common('review'),
                    className: 'max-w-[95%]',
                    onClose: () => dispatch(appActions.closeModal(ModalType.DYNAMIC)),
                    children: () => {
                        return (
                            <SourcesAnswerComponent 
                                numPages={res.items.length}
                                document={{ message: 's', sources: res.items}}
                                loading={false}
                            />
                        )
                    }
                }
            }))
        } else {
            setLoadingDocument(false);
        }
    }
    
    return (
        <>

            <Accordion key={defaultValue} type="single" collapsible defaultValue={defaultValue}>

                {
                    !structured && (
                        <Card className="mb-3">
                            <CardContent>
                                <AccordionItem value={`item_general_knowledge`}
                                            key={`item_general_knowledge`}>
                                    <AccordionTrigger
                                        className="text-lg font-semibold text-purple-700 hover:text-purple-900 capitalize">
                                        <div className="flex items-center gap-2">
                                            <Brain className="w-6 h-6  " />
                                            {agent('general_knowledge')}
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <div
                                            className={`grid grid-cols-1 gap-3 mb-3`}>
                                            {customRetrieverFields(name)}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            </CardContent>
                        </Card>
                    ) 
                }

                {Object.keys(skillRetrieverFields).map((group) => {
                    const currentGroup = skillRetrieverFields[group];
                    if(currentGroup.label === SkillType.SkillStructuredRetriever && structured) {
                        return <RenderField  key={`item_${group}`} group={group} currentGroup={currentGroup} />
                    }

                    if(currentGroup.label === SkillType.SkillUnstructuredRetriever && !structured) {
                        return <RenderField  key={`item_${group}`} group={group} currentGroup={currentGroup} />
                    }
                })}

            </Accordion>
            {
                !structured && (watch?.('new_skill.config.folder') || watch?.('new_skill.config.url')) && (
                    <div>
                        <ButtonComponent
                            size={'sm'}
                            loading={loadingDocument}
                            onClick={showDataVector}
                            className="w-full primary-gradient-button"
                            label={common('review') + ' RAG'}
                            icon={<Search className="mr-2 h-4 w-4" />}
                        />
                    </div>
                )
            }
        </>

    );
};

export default SkillRetrieverComponent;